
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCalendar } from '@/hooks/useCalendar';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import EventDetailsDrawer from '@/components/EventDetailsDrawer';
import { type Event } from '@/lib/types';
import FilterBar from '@/components/FilterBar';
import { Filter } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import CalendarPageHeader from '@/components/calendar/CalendarPageHeader';
import CalendarMainContent from '@/components/calendar/CalendarMainContent';
import { useToast } from '@/hooks/use-toast';
import SurveyModal from '@/components/SurveyModal';
import { sendPageView, sendEventDetailView } from '@/services/analyticsService';
import EventMetaTags from '@/components/EventMetaTags';

const CalendarPage = () => {
  console.log('📅 Calendar page rendering');
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [filterCollapsed, setFilterCollapsed] = useState(false);
  const unmountingRef = useRef(false);
  const { eventSlug } = useParams<{ eventSlug?: string }>();
  const { toast } = useToast();

  const {
    currentDate,
    days,
    daysToShow,
    calendarContainerRef,
    cellWidth,
    filteredEvents,
    handleFilterChange,
    navigatePrevious,
    navigateNext,
    navigateToToday,
    viewMode,
    isFilterActive,
    toggleViewMode,
    allEvents,
    findEventBySlugOrCode
  } = useCalendar();

  console.log('📊 Calendar data:', {
    allEventsCount: allEvents?.length || 0,
    filteredEventsCount: filteredEvents?.length || 0,
    viewMode
  });

  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const eventClickProcessingRef = useRef(false);
  const closeDrawerProcessingRef = useRef(false);

  // Oldal megtekintés esemény küldése
  useEffect(() => {
    sendPageView('Naptár oldal');
  }, []);

  useEffect(() => {
    if (!eventSlug || eventClickProcessingRef.current || closeDrawerProcessingRef.current) return;
    
    if (allEvents.length > 0 && !showDetailsDrawer) {
      const event = findEventBySlugOrCode(eventSlug);
      
      if (event) {
        console.log('Found event from memory:', event);
        setSelectedEvent(event);
        setShowDetailsDrawer(true);
        
        // Analytics esemény: esemény részletes megtekintése
        sendEventDetailView(event);
      } else {
        toast({
          title: "Esemény nem található",
          description: `A(z) "${eventSlug}" esemény nem található.`,
          variant: "destructive"
        });
        navigate('/calendar', { replace: true });
      }
    }
  }, [eventSlug, allEvents, findEventBySlugOrCode, navigate, toast, showDetailsDrawer]);

  useEffect(() => {
    return () => {
      console.log('Calendar page unmounting, setting unmounting flag');
      unmountingRef.current = true;
    };
  }, []);

  const handleEventClick = useCallback((event: Event) => {
    if (eventClickProcessingRef.current) return;
    eventClickProcessingRef.current = true;
    
    console.log('Event clicked:', event);
    
    setSelectedEvent(event);
    setShowDetailsDrawer(true);
    
    // Analytics esemény: esemény részletes megtekintése
    sendEventDetailView(event);
    
    setTimeout(() => {
      if (!unmountingRef.current) {
        const eventIdentifier = event.slug || event.code;
        if (eventIdentifier) {
          const preventScrollState = { preventScroll: true };
          navigate(`/calendar/event/${eventIdentifier}`, { 
            replace: true, 
            state: preventScrollState 
          });
        }
      }
      setTimeout(() => {
        eventClickProcessingRef.current = false;
      }, 100);
    }, 0);
  }, [navigate]);

  const handleCloseDrawer = useCallback(() => {
    if (closeDrawerProcessingRef.current) return;
    closeDrawerProcessingRef.current = true;
    
    setShowDetailsDrawer(false);
    
    if (eventSlug) {
      setTimeout(() => {
        if (!unmountingRef.current) {
          const preventScrollState = { preventScroll: true };
          navigate('/calendar', { 
            replace: true,
            state: preventScrollState
          });
        }
        
        setTimeout(() => {
          closeDrawerProcessingRef.current = false;
        }, 100);
      }, 0);
    } else {
      setTimeout(() => {
        closeDrawerProcessingRef.current = false;
      }, 100);
    }
  }, [eventSlug, navigate]);

  const handleNavigateToRegister = useCallback(() => {
    console.log('Navigating to register page');
    navigate('/register/participant');
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50">
      {selectedEvent && <EventMetaTags event={selectedEvent} />}
      <Navbar />

      <div className="container mx-auto px-4 pt-20 pb-10 mt-10">
        <CalendarPageHeader handleNavigateToRegister={handleNavigateToRegister} />

        <div className={`${isMobile ? '' : 'flex gap-4'}`}>
          {!isMobile && (
            <div className="relative">
              {filterCollapsed && (
                <button
                  onClick={() => setFilterCollapsed(false)}
                  className="fixed z-50 left-0 top-32 bg-medihub-primary text-white py-10 px-2 rounded-r-lg shadow-md flex flex-col items-center justify-center gap-2 hover:bg-medihub-primary/90 transition-all"
                >
                  <Filter className="w-5 h-5" />
                  <div className="vertical-text font-medium">SZŰRŐ</div>
                </button>
              )}

              <div
                className={cn(
                  "transition-all duration-300 ease-in-out",
                  filterCollapsed ? "w-0 opacity-0 invisible" : "w-full md:w-80 lg:w-96 shrink-0 opacity-100 visible",
                  viewMode === 'list' ? "sticky top-24" : ""
                )}
              >
                <FilterBar
                  onFilterChange={handleFilterChange}
                  currentDate={currentDate}
                  className="sticky top-24"
                  onCollapse={() => setFilterCollapsed(true)}
                  isCollapsed={filterCollapsed}
                />
              </div>
            </div>
          )}

          {isMobile && (
            <div className="mb-6">
              <FilterBar
                onFilterChange={handleFilterChange}
                currentDate={currentDate}
              />
            </div>
          )}

          <div className="flex-1 overflow-hidden" ref={calendarContainerRef}>
            <CalendarMainContent
              viewMode={viewMode}
              currentDate={currentDate}
              days={days}
              daysToShow={daysToShow}
              cellWidth={cellWidth}
              filteredEvents={filteredEvents}
              isFilterActive={isFilterActive}
              toggleViewMode={toggleViewMode}
              navigatePrevious={navigatePrevious}
              navigateNext={navigateNext}
              onEventClick={handleEventClick}
              navigateToToday={navigateToToday}
            />
          </div>
        </div>
      </div>

      <EventDetailsDrawer
        event={selectedEvent}
        open={showDetailsDrawer}
        onClose={handleCloseDrawer}
      />

      {/* Visszajelzési űrlap komponens - csak egyszer jelenik meg */}
      <SurveyModal delayMs={30000} dismissDays={30} />

      <Footer />
    </div>
  );
};

export default CalendarPage;
